import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { SEG_LIST } from 'src/store/ActionTypes';

import * as api from 'src/store/api';

function* addModelTarget(action) {
  try {
    const {
      payload: { id, payload },
    } = action;
    yield call(api.addThinkalikeModelTarget, id, payload);
    yield put({
      type: SEG_LIST.ADD_MODEL_TARGET_SUCCEEDED,
      payload,
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.ADD_MODEL_TARGET_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'addModelTargetStatus',
    });
  }
}

function* fetchModels(action) {
  try {
    const {
      payload: { fields = '*', ...query },
    } = action;
    const options = {
      headers: {
        'X-Fields': fields,
      },
    };
    const models = yield call(api.fetchModels, query, options);
    // TODO: fetch the auto LRM sub-models here and append to the list of models only in SEG_LIST state key
    yield put({
      type: SEG_LIST.FETCH_MODELS_SUCCEEDED,
      payload: models,
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.FETCH_MODELS_FAILED,
    });
  }
}

function* fetchSegments(action) {
  try {
    const {
      payload: { fields = '*', ...query },
    } = action;
    const options = {
      headers: {
        'X-Fields': fields,
      },
    };
    const segments = yield call(api.fetchSegments, query, options);
    yield put({
      type: SEG_LIST.FETCH_SEGMENTS_SUCCEEDED,
      payload: segments,
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.FETCH_SEGMENTS_FAILED,
    });
  }
}

function* selectGridRow(action) {
  const { payload: row } = action;
  const {
    segmentsList: { maxGridRowSelection, selectedGridRows },
  } = yield select();
  if (selectedGridRows.length < maxGridRowSelection) {
    yield put({
      type: SEG_LIST.SET_SELECTED_GRID_ROWS,
      payload: [...selectedGridRows, row],
    });
  } else {
    const [, ...restRows] = selectedGridRows;
    yield put({
      type: SEG_LIST.SET_SELECTED_GRID_ROWS,
      payload: [...restRows, row],
    });
  }
}

function* deselectGridRow(action) {
  const { payload: row } = action;
  const {
    segmentsList: { selectedGridRows },
  } = yield select();
  const filteredSelectedGridRows = selectedGridRows.filter(({ id }) => id !== row.id);
  yield put({
    type: SEG_LIST.SET_SELECTED_GRID_ROWS,
    payload: filteredSelectedGridRows,
  });
}

function* editDataset(action) {
  try {
    const {
      payload: { id, payload },
    } = action;
    yield call(api.updateDataset, id, payload);
    yield put({
      type: SEG_LIST.EDIT_DATASET_SUCCEEDED,
      payload: {
        id,
        payload,
      },
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.EDIT_DATASET_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'editDatasetStatus',
    });
  }
}

function* createModel(action) {
  try {
    const { payload } = action;
    yield call(api.addThinkalikeModel, payload);
    yield put({
      type: SEG_LIST.CREATE_MODEL_SUCCEEDED,
      payload,
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.CREATE_MODEL_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'createModelStatus',
    });
  }
}

function* editSegment(action) {
  try {
    const {
      payload: { id, payload },
    } = action;
    yield call(api.updateSegment, id, payload);
    yield put({
      type: SEG_LIST.EDIT_SEGMENT_SUCCEEDED,
      payload: {
        id,
        payload,
      },
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.EDIT_SEGMENT_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'editSegmentStatus',
    });
  }
}

function* editCluster(action) {
  try {
    const {
      payload: { id, parentId, payload },
    } = action;
    yield call(api.updateCluster, id, payload);
    yield put({
      type: SEG_LIST.EDIT_CLUSTER_SUCCEEDED,
      payload: {
        id,
        parentId,
        payload,
      },
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.EDIT_CLUSTER_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'editClusterStatus',
    });
  }
}

function* checkDeleteSegment(action) {
  try {
    const dependents = yield call(api.checkDeleteSegment, action.payload);
    yield put({
      type: SEG_LIST.CHECK_DELETE_SEGMENT_SUCCEEDED,
      payload: dependents,
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.CHECK_DELETE_SEGMENT_FAILED,
      payload: e,
    });
  } finally {
    yield delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'checkDeleteSegmentStatus',
    });
  }
}

function* deleteSegment(action) {
  try {
    yield call(api.deleteSegment, action.payload);
    yield put({
      type: SEG_LIST.DELETE_SEGMENT_SUCCEEDED,
      payload: action.payload,
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.DELETE_SEGMENT_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'deleteSegmentStatus',
    });
  }
}

function* deleteCluster(action) {
  try {
    const { parentId, id } = action.payload;
    yield call(api.deleteCluster, id);
    yield put({
      type: SEG_LIST.DELETE_CLUSTER_SUCCEEDED,
      payload: { parentId, id },
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.DELETE_CLUSTER_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'deleteClusterStatus',
    });
  }
}

function* fetchDownloadUrl(action) {
  try {
    const { entityId, entity, id } = action.payload;
    const downloadUrl = yield call(api.fetchEntityDownloadUrl, entityId, entity, id);
    yield put({
      type: SEG_LIST.FETCH_DOWNLOAD_URL_SUCCEEDED,
      payload: downloadUrl,
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.FETCH_DOWNLOAD_URL_FAILED,
      payload: e.message || e.toString(),
    });
  } finally {
    delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'fetchDownloadUrlStatus',
    });
  }
}

function* deleteDownload(action) {
  try {
    const { entityId, entity, id } = action.payload;
    yield call(api.deleteEntityDownload, entityId, entity, id);
    yield put({
      type: SEG_LIST.DELETE_DOWNLOAD_SUCCEEDED,
      payload: { entityId, id },
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.DELETE_DOWNLOAD_FAILED,
      payload: e.message || e.toString(),
    });
  } finally {
    delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'deleteDownloadStatus',
    });
  }
}
function* toggleVisibilityDownload(action) {
  try {
    const { entityId, entity, id, nextState } = action.payload;
    yield call(api.setVisibilityEntityDownload, entityId, entity, id, nextState);
    yield put({
      type: SEG_LIST.TOGGLE_VISIBILITY_DOWNLOAD_SUCCEEDED,
      payload: { entityId, id },
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.TOGGLE_VISIBILITY_DOWNLOAD_FAILED,
      payload: e.message || e.toString(),
    });
  } finally {
    delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'toggleVisibilityDownloadStatus',
    });
  }
}

function* fetchClientByTenant(action) {
  try {
    const client = yield call(api.fetchClientByTenant, action.payload);
    yield put({
      type: SEG_LIST.FETCH_CLIENT_BY_TENANT_SUCCEEDED,
      payload: client,
    });
  } catch (e) {
    yield put({
      type: SEG_LIST.FETCH_CLIENT_BY_TENANT_FAILED,
      payload: e.message || e.toString(),
    });
  } finally {
    delay(500);
    yield put({
      type: SEG_LIST.UNSET,
      payload: 'fetchClientByTenantStatus',
    });
  }
}

/*
function* uploadNewOutcomes(action) {
  try {
    const {
      segmentList: { datasetName, datasetTag, onboardingType },
    } = yield select();
    const {
      app: { profile, tenant },
    } = yield select();
    // Payload to associate with the upload
    const userPayload = {
      profile,
      datasetName,
      datasetTag,
      onboardingType,
    };
    const channelFactory = () =>
      eventChannel((emitter) => {
        const {
          payload: { files, keyPrefix },
        } = action;
        const promises = files.map(async (file, index) => {
          const fileMetadata = {
            name: file.name,
            type: file.type,
            size: file.size,
          };
          // check file name for csv extension (MIME not always available)
          // file.name.slice(-4).toLowerCase() gives the extension of the file. However,
          // checks on this extension doesn't improve security but may offer friction
          // for customers who are onboarding with non-standard extension. We have a validation
          // that reads the file after upload and that check should validate if the CSV file
          // is valid
          // Carl NOTE: it may create friction but on certain system configurations MIME type isn't
          // available to check. Consequently, the best effort check is for a csv extension.
          if (!file.name.slice(-4).toLowerCase().includes('csv')) {
            Sentry.captureException(
              `File uploaded by tenant ${tenant} was not a csv. Meta: ${JSON.stringify(fileMetadata, null, 2)}.`
            );
            throw new Error(`File ${file.name} was not a csv file.`);
          }
          if (file.name.length > 60) {
            throw new Error('File name may not exceed 60 characters total (including extension).');
          }

          const key = `${keyPrefix}/${encodeURIComponent(file.name.replaceAll(' ', '_'))}`;
          const url = await api.getFileUploadUrl('', key);
          const parsedUrl = new URL(url);
          const fileBucket = parsedUrl.host.split('.')[0];
          const fileName = parsedUrl.pathname;
          await api.addOnboardingMetadata({
            file_bucket: fileBucket,
            file_name: decodeURIComponent(fileName),
            payload: JSON.stringify(userPayload, null, 0),
          });
          const status = await api.uploadFilePII(url, file, (progress) => {
            emitter({ progress, index });
          });
          return { status, bucket: fileBucket, key: decodeURIComponent(fileName) };
        });
        Promise.all(promises)
          .then((statuses) => {
            emitter({ statuses });
            emitter(END);
          })
          .catch((error) => {
            emitter({ error: error || 'Upload Failed' });
          });
        return () => {};
      });
    const channel = channelFactory();
    while (true) {
      const { progress = 0, index = 0, statuses, error } = yield take(channel);
      if (error) {
        yield put({
          type: ONBOARDING.UPLOAD_FILES_FAILED,
          payload: error,
        });
        return;
      }
      if (statuses?.length) {
        const { bucket, key } = statuses[0];
        yield put({
          type: ONBOARDING.UPLOAD_FILES_SUCCEEDED,
          payload: {
            bucket,
            key,
          },
        });
        return;
      }
      yield put({
        type: ONBOARDING.UPDATE_PROGRESS,
        payload: {
          index,
          progress,
        },
      });
    }
  } catch (error) {
    yield put({
      type: ONBOARDING.UPLOAD_FILES_FAILED,
      payload: error,
    });
  } finally {
    delay(500);
    yield put({
      type: ONBOARDING.UNSET,
      payload: 'uploadFileStatus',
    });
  }
}
*/

function* addModelTargetSaga() {
  yield takeLatest(SEG_LIST.ADD_MODEL_TARGET, addModelTarget);
}

function* fetchModelsSaga() {
  yield takeLatest(SEG_LIST.FETCH_MODELS, fetchModels);
}

function* fetchSegmentsSaga() {
  yield takeLatest(SEG_LIST.FETCH_SEGMENTS, fetchSegments);
}

function* onRowSelectSaga() {
  yield takeLatest(SEG_LIST.SELECT_GRID_ROW, selectGridRow);
}

function* onRowDeselectSaga() {
  yield takeLatest(SEG_LIST.DESELECT_GRID_ROW, deselectGridRow);
}

function* editDatasetSaga() {
  yield takeLatest(SEG_LIST.EDIT_DATASET, editDataset);
}

function* createModelSaga() {
  yield takeLatest(SEG_LIST.CREATE_MODEL, createModel);
}

function* editSegmentSaga() {
  yield takeLatest(SEG_LIST.EDIT_SEGMENT, editSegment);
}

function* editClusterSaga() {
  yield takeLatest(SEG_LIST.EDIT_CLUSTER, editCluster);
}

function* checkDeleteSegmentSaga() {
  yield takeLatest(SEG_LIST.CHECK_DELETE_SEGMENT, checkDeleteSegment);
}

function* deleteSegmentSaga() {
  yield takeLatest(SEG_LIST.DELETE_SEGMENT, deleteSegment);
}

function* deleteClusterSaga() {
  yield takeLatest(SEG_LIST.DELETE_CLUSTER, deleteCluster);
}

function* fetchDownloadUrlSaga() {
  yield takeLatest(SEG_LIST.FETCH_DOWNLOAD_URL, fetchDownloadUrl);
}

function* deleteDownloadSaga() {
  yield takeLatest(SEG_LIST.DELETE_DOWNLOAD, deleteDownload);
}

function* toggleVisibilityDownloadSaga() {
  yield takeLatest(SEG_LIST.TOGGLE_VISIBILITY_DOWNLOAD, toggleVisibilityDownload);
}

function* fetchClientByTenantSaga() {
  yield takeLatest(SEG_LIST.FETCH_CLIENT_BY_TENANT, fetchClientByTenant);
}

export default [
  addModelTargetSaga(),
  fetchModelsSaga(),
  fetchSegmentsSaga(),
  onRowSelectSaga(),
  onRowDeselectSaga(),
  editDatasetSaga(),
  createModelSaga(),
  editSegmentSaga(),
  editClusterSaga(),
  deleteSegmentSaga(),
  checkDeleteSegmentSaga(),
  deleteClusterSaga(),
  deleteDownloadSaga(),
  toggleVisibilityDownloadSaga(),
  fetchDownloadUrlSaga(),
  fetchClientByTenantSaga(),
];
